import {ConfirmDialogLabel} from "@components";
import {translate} from "@util";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  SvgIconOwnProps,
} from "@mui/material";
import {DeleteIcon} from "@components/icons";
import {useThemeContext} from "@theme";
import {BaseEntity} from "@core/model";

export type DeleteDialogProps<T extends BaseEntity> = {
  open: boolean;
  data?: Partial<T>;
  label?: ConfirmDialogLabel;
  isLoading: boolean;
  EntityIcon?: React.ComponentType<SvgIconOwnProps>;
  onConfirm: (e: Partial<T>) => void;
  onClose: () => void;
};

function DeleteDialog<T extends BaseEntity>(props: DeleteDialogProps<T>) {
  const {open, EntityIcon, label, data, isLoading, onClose, onConfirm} = props;

  const {theme} = useThemeContext();

  const renderText = () => {
    if (typeof label?.text === "string") {
      return translate(label.text);
    }
    return label?.text ?? translate("deleteDialog.text");
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description">
      <Box
        mt={2}
        bgcolor={theme.palette.secondary.dark}
        width="64px"
        borderRadius="32px"
        height="64px"
        display="flex"
        justifyContent="center"
        alignItems="center"
        margin="16px auto 0 auto">
        {EntityIcon ? (
          <EntityIcon
            fontSize="large"
            sx={{
              color: theme.palette.getContrastText(
                theme.palette.secondary.dark,
              ),
            }}
          />
        ) : (
          <DeleteIcon
            fontSize="large"
            sx={{
              color: theme.palette.getContrastText(
                theme.palette.secondary.dark,
              ),
            }}
          />
        )}
      </Box>
      <DialogTitle align="center">
        {translate(label?.title ?? "deleteDialog.title")}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>{renderText()}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => data && onConfirm(data)}
          startIcon={<DeleteIcon />}
          loading={isLoading}
          variant="contained">
          {translate("common.action.delete")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export {DeleteDialog};
